import { isEmpty, isNil, takeRight } from 'lodash-es'

type SearchParams = Record<string, string | string[] | number | number[] | null | undefined>

export function replaceUrlOrigin<T extends string | null | undefined>(urlString: T, newOrigin?: Maybe<string>) {
  if (!urlString) return urlString

  const url = new URL(urlString)
  return `${newOrigin ?? ''}${url.pathname}${url.search}`
}

export function addSubdomain(originalUrl: string, subdomain?: Maybe<string>) {
  if (!originalUrl || !subdomain) return originalUrl

  const url = new URL(originalUrl)
  url.hostname = `${subdomain}.${url.hostname}`
  return url.toString()
}

export function applySubdomain(originalUrl: string, subdomain: Maybe<string>, baseDomainLength: number) {
  const url = new URL(originalUrl)
  const hostname = url.hostname
  const hostParts = hostname.split('.')
  const baseHostname = takeRight(hostParts, baseDomainLength).join('.')

  if (subdomain) {
    url.hostname = `${subdomain}.${baseHostname}`
  } else {
    url.hostname = baseHostname
  }
  return url.toString()
}

export function stringifySearchParams(searchParams: SearchParams) {
  return Object.keys(searchParams)
    .flatMap(key => {
      const value = searchParams[key]
      if (isNil(value)) return []
      const isArrayValue = Array.isArray(value)
      const valueKey = isArrayValue ? `${key}[]` : key
      return (isArrayValue ? value : [value]).map(
        val => [valueKey, val] as [string, string | number | null | undefined]
      )
    })
    .map(([key, val]) => encodeURIComponent(key) + (isNil(val) ? '' : `=${encodeURIComponent(val)}`))
    .join('&')
}

export function buildUrlWithSearchParams(baseUrl: string, searchParams?: SearchParams) {
  if (!searchParams || isEmpty(searchParams)) return baseUrl

  const urlObj = new URL(baseUrl)
  urlObj.search = stringifySearchParams(searchParams)

  return urlObj.toString()
}

export function removeSearchParams(url: string, searchParamKeys: string | string[]) {
  if (isEmpty(searchParamKeys)) return url

  const urlObj = new URL(url)
  const keys = Array.isArray(searchParamKeys) ? searchParamKeys : [searchParamKeys]
  keys.forEach(key => {
    urlObj.searchParams.delete(key)
  })
  return urlObj.toString()
}

export function keepOnlySearchParams(url: string, keysToKeep: string | string[]) {
  const urlObj = new URL(url)
  const keys = Array.isArray(keysToKeep) ? keysToKeep : [keysToKeep]

  const newSearch = new URLSearchParams()
  for (const key of keys) {
    for (const value of urlObj.searchParams.getAll(key)) {
      newSearch.append(key, value)
    }
  }

  urlObj.search = newSearch.toString()
  return urlObj.toString()
}

// Example:
//    appendQueryParams('https://example.com', { foo: 'bar', baz: ['qux', 'quux'] })
//      => 'https://example.com?foo=bar&baz[]=qux&baz[]=quux'
//    appendQueryParams('https://example.com?foo=bar', { baz: ['qux', 'quux'] })
//      => 'https://example.com?foo=bar&baz[]=qux&baz[]=quux'
export function appendSearchParams(url: string, params?: string | SearchParams) {
  if (isNil(params)) return url

  const searchParams = typeof params === 'string' ? params : stringifySearchParams(params)

  if (!searchParams) return url

  return url.lastIndexOf('?') === -1 ? `${url}?${searchParams}` : `${url}&${searchParams}`
}

export const appendQueryParams = appendSearchParams
